import { useCallback, useEffect, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, DropdownButton, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { ActionModal } from "app/pages/sales-intelligence/common-components/modals/ActionModal/ActionModal";
import { OverflowTooltip } from "app/pages/sales-intelligence/common-components/OverflowTooltip/OverflowTooltip";
import { ScrollDismissBoundary } from "app/pages/sales-intelligence/common-components/scroll-dismiss/ScrollDismissBoundary";
import type { LeadsSearchRow } from "app/pages/sales-intelligence/modules/leads-search";
import { getMainCompanyDomainOrDefault } from "app/pages/sales-intelligence/modules/leads-search/utils/rows";
import { useCreateTagMutation } from "app/pages/sales-intelligence/modules/lists/query/queries/useCreateTagMutation";
import { useDeleteTagMutation } from "app/pages/sales-intelligence/modules/lists/query/queries/useDeleteTagMutation";
import { useSetTagMutation } from "app/pages/sales-intelligence/modules/lists/query/queries/useSetTagMutation";
import { useTagsQuery } from "app/pages/sales-intelligence/modules/lists/query/queries/useTagsQuery";
import { useUpdateTagMutation } from "app/pages/sales-intelligence/modules/lists/query/queries/useUpdateTagMutation";
import trackingService from "app/pages/sales-intelligence/modules/lists/services/tracking/trackingService";
import type { TagColumnValue, TagColumnValueResponse } from "app/pages/sales-intelligence/modules/lists/types/columns";

import { useListParams } from "../../../context/ListParamsContext";

import { StyledBaseCell, StyledDot, StyledDropdownButtonContainer } from "./styles";
import { TagEditForm } from "./TagEditForm";
import { TagOption } from "./TagOption";
import { getTagColor } from "./utils";

const DEFAULT_TAG_COLOR = colorsPalettes.carbon[250];

type EditState = {
    tagId: string;
    label: string;
    color: string;
};

type Props = {
    value: TagColumnValue;
    record: LeadsSearchRow;
};

export const TagCell = ({ value, record }: Props) => {
    const { translate } = useTranslate();

    const listParams = useListParams();

    const domain = getMainCompanyDomainOrDefault(record).domain;

    const [selectedValue, setSelectedValue] = useState<TagColumnValue>(value);
    const [isOpen, setIsOpen] = useState(false);
    const [isInputVisible, setIsInputVisible] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [newTagColor, setNewTagColor] = useState<string>(DEFAULT_TAG_COLOR);
    const [editState, setEditState] = useState<EditState | null>(null);
    const [tagToDelete, setTagToDelete] = useState<TagColumnValueResponse | null>(null);

    const { data: tags } = useTagsQuery(listParams.id);
    const { mutate: setTag } = useSetTagMutation(listParams, domain);
    const { mutate: createTag } = useCreateTagMutation(listParams.id);
    const { mutate: updateTag } = useUpdateTagMutation(listParams.id);
    const { mutate: deleteTag } = useDeleteTagMutation(listParams.id);

    useEffect(() => {
        setSelectedValue(value);
    }, [value, record]);

    const resetInputState = () => {
        setIsInputVisible(false);
        setInputValue("");
        setNewTagColor(DEFAULT_TAG_COLOR);
    };

    const resetEditState = () => {
        setEditState(null);
    };

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);

        if (open) {
            trackingService.trackTagCellDDClick();
        } else {
            resetInputState();
            resetEditState();
        }
    };

    const handleTagAdd = (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const trimmedValue = inputValue.trim();

        if (trimmedValue) {
            createTag({ tagTitle: trimmedValue, colorHex: newTagColor });

            resetInputState();
            trackingService.trackTagCellAddItem(trimmedValue, record.company ?? "");
        }
    };

    const handleSelect = (tag: TagColumnValueResponse) => {
        setIsOpen(false);
        setSelectedValue({ id: tag.Id, title: tag.Title, colorHex: tag.ColorHex });
        setTag({ tagId: tag.Id, domain, text: tag.Title, colorHex: tag.ColorHex });

        trackingService.trackTagCellItemSelect(tag.Title, record.company ?? "");
    };

    const handleEditClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, tag: TagColumnValueResponse) => {
        event.stopPropagation();
        event.preventDefault();
        resetInputState();
        setEditState({
            tagId: tag.Id,
            label: tag.Title,
            color: getTagColor(tag),
        });
    }, []);

    const handleEditSave = useCallback(() => {
        if (!editState) {
            return;
        }

        const trimmedLabel = editState.label.trim();
        if (!trimmedLabel) {
            return;
        }

        updateTag({
            tag: { Id: editState.tagId, Title: trimmedLabel, ColorHex: editState.color },
        });
        resetEditState();
    }, [editState, updateTag]);

    const handleEditCancel = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();
        resetEditState();
    }, []);

    const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, tag: TagColumnValueResponse) => {
        event.stopPropagation();
        event.preventDefault();
        setTagToDelete(tag);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (tagToDelete) {
            deleteTag(tagToDelete.Id);
            setTagToDelete(null);
        }
    }, [tagToDelete, deleteTag]);

    const handleDeleteCancel = useCallback(() => {
        setTagToDelete(null);
    }, []);

    return (
        <StyledBaseCell>
            <ScrollDismissBoundary
                isOpen={isOpen}
                onClose={() => handleOpenChange(false)}
            >
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={handleOpenChange}
                    width="258px"
                >
                    <Combobox.Trigger>
                        <StyledDropdownButtonContainer $isOpen={isOpen}>
                            <DropdownButton isOpen={isOpen}>
                                <StyledDot
                                    color={getTagColor({
                                        Id: selectedValue.id,
                                        Title: selectedValue.title,
                                        ColorHex: selectedValue.colorHex,
                                    })}
                                />
                                <OverflowTooltip
                                    text={selectedValue.title}
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[400]}
                                />
                            </DropdownButton>
                        </StyledDropdownButtonContainer>
                    </Combobox.Trigger>

                    <Combobox.Content>
                        {tags?.map((tag) => (
                            <TagOption
                                key={tag.Id}
                                tag={tag}
                                selectedValue={selectedValue}
                                handleSelect={handleSelect}
                                handleEditClick={handleEditClick}
                                handleDeleteClick={handleDeleteClick}
                            />
                        ))}
                    </Combobox.Content>

                    {editState !== null ? (
                        <TagEditForm
                            mode="edit"
                            selectedColor={editState.color}
                            defaultInputValue={editState.label}
                            isSaveDisabled={!editState.label.trim()}
                            onColorChange={(color) => setEditState((prev) => (prev ? { ...prev, color } : prev))}
                            onInputChange={(value) => setEditState((prev) => (prev ? { ...prev, label: value } : prev))}
                            onSave={handleEditSave}
                            onCancel={handleEditCancel}
                        />
                    ) : null}

                    {isInputVisible ? (
                        <TagEditForm
                            mode="create"
                            selectedColor={newTagColor}
                            isSaveDisabled={!inputValue.trim()}
                            onColorChange={setNewTagColor}
                            onInputChange={setInputValue}
                            onSave={handleTagAdd}
                            onCancel={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                resetInputState();
                            }}
                        />
                    ) : (
                        <Flex padding="8px">
                            <IconButton
                                type="flat"
                                iconName="plus"
                                iconSize="10px"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    resetEditState();
                                    setIsInputVisible(true);
                                }}
                            >
                                {translate("si.lists.tag.cell.add.new.button")}
                            </IconButton>
                        </Flex>
                    )}
                </Combobox>
            </ScrollDismissBoundary>
            <ActionModal
                isOpen={tagToDelete !== null}
                title={translate("si.lists.tag.delete.modal.title", { tag: tagToDelete?.Title })}
                confirmBtnText={translate("si.common.button.delete")}
                cancelBtnText={translate("si.common.button.cancel")}
                cancelBtnType="flatDark"
                confirmBtnType="warning"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                data-automation="lists-tag-delete-modal"
            >
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[400]}
                >
                    {translate("si.lists.tag.delete.modal.description", { tag: tagToDelete?.Title })}
                </Text>
            </ActionModal>
        </StyledBaseCell>
    );
};
